// Phase1Observability1:progress_eta_v1
// Shared progress + ETA renderer used by Library cards, SeriesDetail topic
// rows, and CreateVideo.
//
// eta_state values handled:
//   "running" | "estimating" | "stalled" | "blocked" | "needs_human" | "terminal"

import { Progress } from "@/components/ui/progress";

const STAGE_LABELS = {
  script: "Generating script",
  imaging: "Creating scene art",
  clip_animation: "Animating scenes",
  voiceover: "Adding voiceover",
  stitching: "Mixing & stitching",
  outro: "Adding outro",
  thumbnail: "YouTube thumbnail",
};

// Friendly text for a few well-known needs_human / failure reason codes.
// Anything not matched falls back to a trimmed slice of the raw string.
function humanReason(raw) {
  if (!raw) return null;
  const s = String(raw).toLowerCase();
  if (s.startsWith("phase4_regen_multi_guard_fail")) return "regen failed quality checks";
  if (s.includes("thumbnail_generation_failed"))     return "thumbnail couldn't be generated";
  if (s.includes("count_verifier") || s.includes("scene_count")) return "scene count didn't match script";
  if (s.includes("forbidden_token") || s.includes("test_dom"))   return "test marker leaked into final assets";
  if (s.includes("duration_gate") || s.includes("min_duration")) return "video too short for long-form";
  if (s.includes("motion_intensity") || s.includes("dead_zone")) return "video has dead-motion zones";
  if (s.includes("outro_cta"))                       return "outro CTA missing or silent";
  if (s.includes("thumbnail_present"))               return "thumbnail missing or unreadable";
  return raw.length > 60 ? raw.slice(0, 57) + "…" : raw;
}

function fmtDuration(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return null;
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return `${m}m ${r}s`;
  const h = Math.floor(m / 60);
  const mr = m % 60;
  return `${h}h ${mr}m`;
}

export default function JobProgressBar({ video, compact = false }) {
  if (!video) return null;
  const status = video.status;
  const etaState = video.eta_state;
  const TERMINAL_STATUSES_FE = [
    "ready", "failed", "archived", "archived_legacy",
    "archived_duplicate", "archived_failed", "archived_orphan",
    "archived_master", "archived_partial", "archived_superseded",
  ];
  const terminal = etaState === "terminal" || TERMINAL_STATUSES_FE.includes(status);
  if (terminal) return null;

  const pct = Math.max(0, Math.min(100, video.progress_pct ?? 0));
  const steps = video.steps || [];
  const total = steps.length || 7;
  const done = steps.filter((s) => s.status === "done").length;
  const stageKey = video.current_stage;
  const stageLabel = stageKey ? (STAGE_LABELS[stageKey] || stageKey) : null;
  const stageIdx = stageKey ? steps.findIndex((s) => s.key === stageKey) + 1 : 0;
  const stageNum = stageIdx > 0 ? stageIdx : done + 1;
  const stagePrefix = stageLabel ? `Stage ${stageNum}/${total} · ${stageLabel}` : null;

  let line = null;
  let dotClass = "bg-orange-500 animate-pulse";

  if (etaState === "needs_human") {
    const reason = humanReason(video.needs_human_reason);
    line = reason ? `Needs your review · ${reason}` : "Needs your review";
    dotClass = "bg-rose-500";
  } else if (etaState === "blocked") {
    // Killswitch paused. If we have a stage, surface it; else just say paused.
    line = stagePrefix
      ? `Pipeline paused — ${stageLabel} · waiting for resume`
      : "Pipeline paused — waiting for resume";
    dotClass = "bg-amber-500";
  } else if (etaState === "stalled") {
    if (video.awaiting_retry && video.next_retry_at) {
      const eta = video.eta_seconds;
      const retryTxt = eta != null && eta > 0
        ? `retrying in ${fmtDuration(eta)}`
        : "retrying soon…";
      line = stagePrefix ? `${stagePrefix} · ${retryTxt}` : retryTxt;
    } else {
      line = stagePrefix ? `${stagePrefix} · stalled` : "Stalled";
    }
    dotClass = "bg-rose-500";
  } else if (etaState === "estimating") {
    line = stagePrefix ? `${stagePrefix} · estimating…` : "Estimating…";
  } else {
    // "running" or unknown — best-effort
    const eta = video.eta_seconds;
    const etaTxt = eta != null ? `~${fmtDuration(eta)} left` : null;
    if (stagePrefix && etaTxt)      line = `${stagePrefix} · ${etaTxt}`;
    else if (stagePrefix)            line = stagePrefix;
    else if (etaTxt)                 line = etaTxt;
    else                             line = "Working…";
  }

  return (
    <div
      data-testid={`job-progress-${video.id}`}
      className={compact ? "space-y-1" : "space-y-1.5"}
    >
      <Progress
        value={pct}
        className={
          compact
            ? "h-1 bg-orange-100 [&>div]:bg-gradient-to-r [&>div]:from-orange-500 [&>div]:to-rose-500"
            : "h-1.5 bg-orange-100 [&>div]:bg-gradient-to-r [&>div]:from-orange-500 [&>div]:to-rose-500"
        }
      />
      <div className="flex items-center gap-1.5 text-xs text-stone-600">
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${dotClass}`} />
        <span data-testid={`job-progress-line-${video.id}`} className="truncate">
          {line}
        </span>
      </div>
    </div>
  );
}
